import {
  createAction,
  createSlice,
  PayloadAction,
  combineReducers,
} from '@reduxjs/toolkit';

import {
  CFMUpdatePayload,
  CFMUpdateState,
  CFMUpdateSteps,
  ClinicBillingType,
  ClinicFormValues,
  ProfessionalFormValues,
} from '../types';
import { RequestStatus } from 'shared/constants/State';
import { zipCodeReducer } from '../services';

export const initialState: CFMUpdateState = {
  steps: null,
  isAlertVisible: false,
  clinic: null,
  professional: null,
  clinicBilling: null,
  step: CFMUpdateSteps.none,
  status: RequestStatus.Ready,
  clinicUpdateDone: false,
  clinicBillingUpdateDone: false,
  professionalUpdateDone: false,
  error: '',
  possibleSteps: [],
};

export const getCFMUpdateData = createAction('CFMUpdate/getData');

export const submitProfessional = createAction<ProfessionalFormValues>(
  'CFMUpdate/submitProfessional',
);

export const submitClinic = createAction<ClinicFormValues>(
  'CFMUpdate/submitClinic',
);

export const submitClinicBilling = createAction<ClinicBillingType>(
  'CFMUpdate/submitClinicBilling',
);

export const checkNextStep = createAction('CFMUpdate/checkNextStep');

const cfmUpdateSlice = createSlice({
  name: 'cfmUpdate',
  initialState,
  reducers: {
    setData: (state, action: PayloadAction<CFMUpdatePayload>) => {
      const { clinic, professional, clinicBilling } = action.payload;
      return {
        ...state,
        clinic,
        clinicBilling,
        professional,
        isAlertVisible: !!clinic || !!professional || !!clinicBilling,
      };
    },
    open: (
      state,
      action: PayloadAction<{
        steps: Record<string, JSX.Element>;
        step?: CFMUpdateSteps;
      }>,
    ) => ({
      ...state,
      step: action.payload.step ?? CFMUpdateSteps.start,
      steps: action.payload.steps,
    }),
    dataUpdateError: (state, action: PayloadAction<string>) => ({
      ...state,
      status: RequestStatus.Error,
      error: action.payload,
    }),
    close: (state) => ({ ...state, step: CFMUpdateSteps.close }),
    requestPending: (state) => ({
      ...state,
      status: RequestStatus.Pending,
      error: '',
    }),
    professionalUpdatedSuccess: (state) => ({
      ...state,
      professionalUpdateDone: true,
      status: RequestStatus.Success,
      error: '',
    }),
    clinicUpdatedSuccess: (state) => ({
      ...state,
      clinicUpdateDone: true,
      status: RequestStatus.Success,
      error: '',
    }),
    clinicBillingUpdateSuccess: (state) => ({
      ...state,
      clinicBillingUpdateDone: true,
      status: RequestStatus.Success,
      error: '',
    }),
    setStep: (state, action: PayloadAction<CFMUpdateSteps>) => ({
      ...state,
      step: action.payload,
    }),
    setClinicBillingFormValues: (
      state,
      action: PayloadAction<ClinicBillingType | null>,
    ) => ({
      ...state,
      clinicBilling: action.payload,
    }),
    setAlertHidden: (state) => ({
      ...state,
      isAlertVisible: false,
    }),
    setPossibleSteps: (state, action: PayloadAction<string[]>) => ({
      ...state,
      possibleSteps: action.payload,
    }),
  },
});

export const {
  setData,
  open,
  close,
  requestPending,
  dataUpdateError,
  professionalUpdatedSuccess,
  clinicUpdatedSuccess,
  clinicBillingUpdateSuccess,
  setStep,
  setClinicBillingFormValues,
  setAlertHidden,
  setPossibleSteps,
} = cfmUpdateSlice.actions;

const reducer = combineReducers({
  main: cfmUpdateSlice.reducer,
  zipCode: zipCodeReducer,
});

export const { reducer: mainReducer } = cfmUpdateSlice;
export default reducer;
